import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { User } from '@vsolv/core/users/domain';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { Config } from '@vsolv/packages/portal-config/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { NavItem } from '@vsolv/vectors-ui/app-nav';
import { SlideOutComponent } from '@vsolv/vectors-ui/slide-out';
import { SecurityService } from '@wsphere/staff/web';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LEFT_NAV_CONFIG } from './left-nav.config';

export interface AppConfig {
  logo: string;
  production: boolean;
}
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './left-nav.component.html',
  styleUrls: ['./left-nav.component.scss'],
  standalone: false,
})
export class LeftNavLayoutComponent implements OnDestroy, OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private firebaseSvc: FirebaseService,
    private securitySvc: SecurityService,
    private breakpointObserver: BreakpointObserver,
    private toastSvc: ToastService
  ) {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(e => e instanceof NavigationStart)
      )
      .subscribe(_ => this.slideOut?.close());
  }

  @HostBinding('class') styles = 'overscroll-contain';
  @ViewChild('slideOut') slideOut?: SlideOutComponent;

  config: AppConfig = {
    logo: 'assets/logo.svg',
    production: environment.production,
  };

  userData$ = this.route.data.pipe(map(data => data['currentUser']));
  readonly config$ = this.route.data.pipe(map(data => data['config'] as Config.PortalConfig));
  private readonly globalDistributors$ = this.securitySvc.globalDistributors$;

  destroy$ = new Subject<void>();
  reroute$ = this.globalDistributors$
    .pipe(takeUntil(this.destroy$), distinctUntilChanged())
    .subscribe(globalDistributors => {
      if (!this.compareGlobalDistributors(globalDistributors?.map(dist => dist.permissionKey) || null)) {
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/refresh', { skipLocationChange: true, state: { isLoading: true } }).then(() => {
          setTimeout(() => {
            this.router.navigateByUrl(currentUrl);
          }, 200);
        });
      }
    });
  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => state.matches));

  open = false;
  menuBtnHover = false;
  navCollapsed = false;
  private collapsedStorageKey: string | null = null;

  private homeNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'dsh_ViewDashboard',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return { icon: 'home-line', label: 'Home', routerLink: '' } as NavItem;
    })
  );

  private policiesNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'pol_View',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        icon: 'file-shield-02',
        label: 'Policies',
        routerLink: 'policies',
      } as NavItem;
    })
  );

  private distributorsNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'dist_ViewDistributors',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        icon: 'package',
        label: 'Distributors',
        routerLink: 'distributors',
      } as NavItem;
    })
  );

  private claimNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'clm_ViewClaim',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        icon: 'file-06',
        label: 'Claims',
        routerLink: 'claims',
      } as NavItem;
    })
  );

  private salesNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'py_ViewSales',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        label: 'Payments',
        icon: 'wallet-03',
        routerLink: 'sales',
      } as NavItem;
    })
  );

  private customersNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'cus_ViewDetails',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        label: 'Customers',
        icon: 'users-01',
        routerLink: 'customers',
      } as NavItem;
    })
  );

  // Global check on purpose: providers are tenant-wide and must not follow the distributor picker.
  private serviceProvidersNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async _globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess('svp_ViewServiceProviders', null);
      if (!hasAccess) return null;

      return {
        label: 'Service Providers',
        icon: 'tool-02',
        routerLink: 'service-providers',
      } as NavItem;
    })
  );

  private warrantiesNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'wrt_ViewWarranty',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        label: 'Warranties',
        icon: 'folder-shield',
        routerLink: 'warranty',
      } as NavItem;
    })
  );

  private documentsNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async _globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess('str_View', null);
      if (!hasAccess) return null;

      return {
        label: 'Documents',
        icon: 'upload-cloud-01',
        routerLink: 'documents',
      } as NavItem;
    })
  );

  private reportsNavItem$: Observable<NavItem | null> = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      const hasAccess = await this.securitySvc.hasAccess(
        'rep_ViewReports',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );
      if (!hasAccess) return null;

      return {
        label: 'Reports',
        icon: 'table',
        routerLink: 'reporting',
      } as NavItem;
    })
  );

  navItems$: Observable<NavItem[]> = combineLatest([
    this.homeNavItem$,
    this.policiesNavItem$,
    this.distributorsNavItem$,
    this.claimNavItem$,
    this.salesNavItem$,
    this.customersNavItem$,
    this.serviceProvidersNavItem$,
    this.warrantiesNavItem$,
    this.documentsNavItem$,
    this.reportsNavItem$,
  ]).pipe(
    map(
      ([
        homeNavItem,
        policiesNavItem,
        distributorsNavItem,
        claimNavItem,
        salesNavItem,
        customersNavItem,
        serviceProvidersNavItem,
        warrantiesNavItem,
        documentsNavItem,
        reportsNavItem,
      ]) => {
        return [
          ...(homeNavItem ? [homeNavItem] : []),
          ...(policiesNavItem ? [policiesNavItem] : []),
          ...(distributorsNavItem ? [distributorsNavItem] : []),
          ...(claimNavItem ? [claimNavItem] : []),
          ...(salesNavItem ? [salesNavItem] : []),
          ...(customersNavItem ? [customersNavItem] : []),
          ...(serviceProvidersNavItem ? [serviceProvidersNavItem] : []),
          ...(warrantiesNavItem ? [warrantiesNavItem] : []),
          ...(documentsNavItem ? [documentsNavItem] : []),
          ...(reportsNavItem ? [reportsNavItem] : []),
        ];
      }
    )
  );

  canSellWarranty$ = this.globalDistributors$.pipe(
    switchMap(async globalDistributors => {
      let hasAccess = await this.securitySvc.hasAccess(
        'wrt_CreateWarranty',
        globalDistributors?.map(dist => dist.permissionKey) || null
      );

      if (!hasAccess) {
        hasAccess = await this.securitySvc.hasAccess(
          'wrt_CreateDraftWarranty',
          globalDistributors?.map(dist => dist.permissionKey) || null
        );
      }

      return hasAccess;
    })
  );

  canOpenSettings$: Observable<boolean> = this.globalDistributors$.pipe(
    switchMap(async () => await this.securitySvc.hasAccess('stf_ViewSettings', null))
  );

  isFullAdmin$: Observable<boolean> = this.globalDistributors$.pipe(
    switchMap(async () => await this.securitySvc.hasAccess('*', null))
  );

  showDistributorSettings$ = this.globalDistributors$.pipe(
    switchMap(async distributors => {
      if (distributors?.length)
        return await this.securitySvc.hasAccess('dist_ViewDistributor', [distributors[0].permissionKey]);
      else return false;
    })
  );

  ngOnInit() {
    const snapshotUser = this.route.snapshot.data['currentUser'] as User.Model | null;
    if (snapshotUser) this.setCollapsedFromStorage(snapshotUser);

    this.userData$
      .pipe(
        takeUntil(this.destroy$),
        filter((user): user is User.Model => !!user),
        tap(user => this.setCollapsedFromStorage(user))
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async signOut() {
    await this.firebaseSvc.signOut();
    await this.router.navigate(['sign-in']);
  }

  // The Support link is a mailto:, which silently does nothing without a default mail app.
  // Copy the address alongside it so there is always a usable outcome.
  async copySupportEmail(email: string) {
    const { copied } = LEFT_NAV_CONFIG.support.toasts;

    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Clipboard is unavailable outside secure contexts - leave the mailto: to do its thing
      return;
    }

    this.toastSvc.show({ type: 'success', title: copied.title, text: copied.text(email) });
  }

  private compareGlobalDistributors(globalDistributors: string[] | null) {
    const storedKeys: string[] = JSON.parse(sessionStorage.getItem('globalDistributors') || '[]');
    let same = true;
    if (!globalDistributors || !globalDistributors.length) {
      if (!storedKeys.length) return true;
      else return false;
    }
    if (storedKeys.length !== globalDistributors.length) return false;

    storedKeys.forEach(stored => {
      if (!globalDistributors.some(dist => dist === stored)) {
        same = false;
        return;
      }
    });

    return same;
  }

  toggleNavCollapsed() {
    this.navCollapsed = !this.navCollapsed;
    this.persistCollapsedState();
  }

  private setCollapsedFromStorage(user: User.Model) {
    this.collapsedStorageKey = `nav-collapsed-${user.id}`;
    const stored = sessionStorage.getItem(this.collapsedStorageKey) ?? localStorage.getItem(this.collapsedStorageKey);
    this.navCollapsed = stored === 'true';
  }

  private persistCollapsedState() {
    if (!this.collapsedStorageKey) return;
    sessionStorage.setItem(this.collapsedStorageKey, `${this.navCollapsed}`);
    localStorage.setItem(this.collapsedStorageKey, `${this.navCollapsed}`);
  }
}
